import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import {
  Scale,
  Stethoscope,
  BookOpen,
  AlertCircle,
  CheckCircle,
  FileText,
  HeartHandshake,
  ChevronDown,
} from "lucide-react";
import AppLayout from '@components/AppLayout';
import '../styles/request-assistance.css';
import { API_BASE_URL } from '../../src/config';

const categories = [
  {
    id: "legal",
    label: "Legal Consultation",
    icon: Scale,
    description: "Workplace disputes, contract review, legal advice",
    color: "is-blue",
  },
  {
    id: "medical",
    label: "Medical Assistance",
    icon: Stethoscope,
    description: "Healthcare costs, emergency support, treatment coverage",
    color: "is-green",
  },
  {
    id: "education",
    label: "Educational Scholarship",
    icon: BookOpen,
    description: "Tuition assistance, training programs, skill development",
    color: "is-purple",
  },
  {
    id: "emergency",
    label: "Emergency Support",
    icon: AlertCircle,
    description: "Urgent financial aid, disaster relief, crisis assistance",
    color: "is-orange",
  },
];

const URGENCY_OPTIONS = ["Normal", "High", "Critical"];

const getUserPersonalInfo = (user) => ({
  fullName: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : "",
  email: user?.email || "",
  phone: user?.phone || "",
});

const REQUIRED_FIELD_IDS = [
  'urgency',
  'subject',
  'customRequest',
];

function RequestUrgencyDropdown({ value, options, onChange, hasError = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const handleSelect = (nextValue) => {
    onChange({ target: { name: 'urgency', value: nextValue } });
    setIsOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className={`request-assistance-urgency ${isOpen ? 'request-assistance-urgency--open' : ''} ${hasError ? 'request-assistance-urgency--error' : ''}`}
    >
      <button
        type="button"
        className="request-assistance-urgency__trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Urgency Level"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span>{value}</span>
        <ChevronDown size={15} className="request-assistance-urgency__icon" aria-hidden="true" />
      </button>

      {isOpen && (
        <ul className="request-assistance-urgency__menu" role="listbox" aria-label="Urgency Level">
          {options.map((option) => (
            <li key={option} role="option" aria-selected={option === value}>
              <button
                type="button"
                className={`request-assistance-urgency__option ${option === value ? 'request-assistance-urgency__option--active' : ''}`}
                onClick={() => handleSelect(option)}
              >
                {option}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

RequestUrgencyDropdown.propTypes = {
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
};

function RequestCategoryDropdown({ value, options, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const selectedOption = options.find((option) => option.id === value) || null;

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setIsOpen(false);
  };

  return (
    <div ref={rootRef} className={`request-assistance__category-dropdown ${isOpen ? 'request-assistance__category-dropdown--open' : ''}`}>
      <button
        type="button"
        className="request-assistance__category-dropdown-trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Request Type"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span>{selectedOption?.label || 'Select request type'}</span>
        <ChevronDown size={15} className="request-assistance__category-dropdown-icon" aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="request-assistance__category-dropdown-menu" role="listbox" aria-label="Request Type">
          {options.map((category) => {
            const Icon = category.icon;
            const isSelected = value === category.id;

            return (
              <button
                key={category.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`request-assistance__category-card ${isSelected ? 'active' : ''}`}
                onClick={() => handleSelect(category.id)}
              >
                <Icon size={16} className="request-assistance__category-icon" />
                <span className="request-assistance__category-label">{category.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

RequestCategoryDropdown.propTypes = {
  value: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};

RequestCategoryDropdown.defaultProps = {
  value: null,
};

export default function RequestAssistance({ user, onLogout }) {
  const navigate = useNavigate();
  const personalInfo = getUserPersonalInfo(user);
  const [isMobileView, setIsMobileView] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [shouldAnimateForm, setShouldAnimateForm] = useState(false);
  const [formData, setFormData] = useState({
    urgency: "Normal",
    subject: "",
    customRequest: "",
    attachments: [],
  });
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [submissionMeta, setSubmissionMeta] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const submitLockRef = useRef(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');

    const syncViewport = () => {
      setIsMobileView(mediaQuery.matches);
    };

    syncViewport();
    mediaQuery.addEventListener('change', syncViewport);
    return () => mediaQuery.removeEventListener('change', syncViewport);
  }, []);

  useEffect(() => {
    if (!showConfirmModal && !showSuccessModal) {
      document.body.style.overflow = '';
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showConfirmModal, showSuccessModal]);

  useEffect(() => {
    if (!showConfirmModal && !showSuccessModal) return undefined;

    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      if (isSubmitting) return;
      if (showConfirmModal) {
        setShowConfirmModal(false);
        return;
      }
      if (showSuccessModal) {
        setShowSuccessModal(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showConfirmModal, showSuccessModal, isSubmitting]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleFileAttach = (files) => {
    const newFiles = Array.from(files);
    const validFiles = newFiles.filter((file) => file.size <= 10 * 1024 * 1024); // 10MB limit
    setAttachedFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileAttach(e.dataTransfer.files);
  };

  const handleCategorySelect = (categoryId) => {
    setShouldAnimateForm(!selectedCategory);
    setSelectedCategory(categoryId);
  };

  const validateRequestForm = () => {
    if (!selectedCategory) {
      alert("Please select a request category");
      return false;
    }

    const missingRequiredFields = REQUIRED_FIELD_IDS.filter((fieldId) => !String(formData[fieldId] || '').trim());
    if (missingRequiredFields.length > 0) {
      const nextErrors = {};
      missingRequiredFields.forEach((fieldId) => {
        nextErrors[fieldId] = true;
      });
      setValidationErrors(nextErrors);
      return false;
    }

    setValidationErrors({});

    return true;
  };

  const resetRequestForm = () => {
    setSelectedCategory(null);
    setShouldAnimateForm(false);
    setFormData({
      urgency: "Normal",
      subject: "",
      customRequest: "",
      attachments: [],
    });
    setAttachedFiles([]);
    setSubmissionMeta(null);
    setValidationErrors({});
  };

  const submitRequest = async () => {
    if (submitLockRef.current) {
      return;
    }
    if (!validateRequestForm()) {
      return;
    }

    submitLockRef.current = true;
    setIsSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('category', selectedCategory);
      formDataToSend.append('priority', formData.urgency);
      formDataToSend.append('message', formData.customRequest);
      formDataToSend.append('description', formData.customRequest);
      formDataToSend.append('detailedDescription', formData.customRequest);
      formDataToSend.append('userId', user.id);
      formDataToSend.append('fullName', personalInfo.fullName);
      formDataToSend.append('email', personalInfo.email);
      formDataToSend.append('phone', personalInfo.phone);
      formDataToSend.append('membershipId', user?.digitalId || '');
      formDataToSend.append('dateOfRequest', new Date().toISOString().split('T')[0]);
      
      attachedFiles.forEach((file) => {
        formDataToSend.append('files', file);
      });

      const response = await fetch(`${API_BASE_URL}/api/tickets`, {
        method: 'POST',
        body: formDataToSend
      });

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to submit ticket');
        } else {
            const text = await response.text();
            console.error("Non-JSON response:", text);
            throw new Error(`Server returned ${response.status}: ${response.statusText}. Check console for details.`);
        }
      }

      const payload = await response.json();
      setSubmissionMeta({
        ticketNo: payload.ticketNo,
        department: payload.department,
        slaDeadline: payload.slaDeadline,
      });
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Error submitting ticket:", error);
      alert(`Failed to submit ticket: ${error.message}`);
    } finally {
      setIsSubmitting(false);
      submitLockRef.current = false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLockRef.current || isSubmitting) {
      return;
    }
    if (!validateRequestForm()) {
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmSubmission = async () => {
    if (submitLockRef.current || isSubmitting) {
      return;
    }
    setShowConfirmModal(false);
    await submitRequest();
  };

  const selectedCategoryData = categories.find((c) => c.id === selectedCategory);
  const requestSummaryItems = [
    { label: 'Request Type', value: selectedCategoryData?.label || '—', highlighted: true },
    { label: 'Urgency Level', value: formData.urgency || '—', highlighted: true },
    { label: 'Subject', value: formData.subject || '—' },
  ];

  const isVerified = user?.isApproved === 'active' || user?.isApproved === 'approved' || user?.isApproved === true || user?.isApproved === 1 || user?.isApproved === '1';
  const isIncomplete = user?.isApproved === 'incomplete' || ((user?.isApproved === 'pending' || user?.isApproved === 0 || user?.isApproved === false) && !user?.dateOfBirth);

  if (!isVerified) {
    return (
      <AppLayout title="Request & Assistance" user={user} onLogout={onLogout}>
        <div className="request-assistance-page">
          <div className="rejection-lockout" style={{
            maxWidth: '600px',
            margin: '64px auto',
            background: 'var(--color-surface, #ffffff)', // Fallback if var not set
            padding: '48px',
            borderRadius: '24px',
            textAlign: 'center',
            boxShadow: 'var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06))',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              width: '72px',
              height: '72px',
              background: '#FFFBEB',
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              margin: '0 auto 24px',
              color: '#F59E0B'
            }}>
              <AlertCircle size={36} />
            </div>
            <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '16px', color: '#111827' }}>Verification Required</h1>
            <p style={{ color: '#6B7280', fontSize: '16px', marginBottom: '32px', lineHeight: '1.6' }}>
              {isIncomplete 
                ? "To submit assistance requests, your membership status must be verified. Please complete your profile to unlock this feature."
                : "Your membership application is currently under review. You will be able to submit requests once your membership is approved."}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              {isIncomplete ? (
                <button 
                  type="button"
                  onClick={() => navigate('/complete-profile')}
                  style={{ 
                    backgroundColor: '#2563eb', 
                    color: 'white', 
                    padding: '12px 24px', 
                    borderRadius: '999px',
                    border: 'none',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Complete Verification
                </button>
              ) : (
                <button 
                  type="button"
                  onClick={() => navigate('/membership-form')}
                  style={{ 
                    backgroundColor: '#2563eb', 
                    color: 'white', 
                    padding: '12px 24px', 
                    borderRadius: '999px',
                    border: 'none',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  View Application
                </button>
              )}
              <button 
                type="button"
                onClick={() => navigate('/dashboard')}
                style={{ 
                  backgroundColor: 'white', 
                  color: '#374151', 
                  padding: '12px 24px', 
                  borderRadius: '999px',
                  border: '1px solid #d1d5db',
                  fontWeight: '600',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </AppLayout>
    );

  }

  return (
    <AppLayout title="Request & Assistance" user={user} onLogout={onLogout}>
      <div className="request-assistance-page">
        <div className="request-assistance__content-layout">
          <div className="request-assistance__main-column">
            <section className="request-assistance__intro">
              <div className="request-assistance__intro-icon">
                <HeartHandshake size={20} />
              </div>
              <div>
                <h2>Need Help?</h2>
                <p>Submit requests for legal consultation, medical assistance, educational scholarships, or emergency support. Our team will review and respond within 2-3 business days.</p>
              </div>
            </section>

            <div className="request-assistance__categories-header request-assistance__categories-header--outside">
              <h3>Select Request Type</h3>
              <button
                type="button"
                className="request-assistance__header-button"
                onClick={() => navigate('/my-requests')}
              >
                View My Requests
              </button>
            </div>

            <section className="request-assistance__request-panel" aria-live="polite">
              {/* Category Selection */}
              <section className="request-assistance__categories">
                {isMobileView ? (
                  <div className="request-assistance__category-select-wrap">
                    <span className="request-assistance__form-label">
                      Request Type
                    </span>
                    <RequestCategoryDropdown
                      value={selectedCategory || ''}
                      options={categories}
                      onChange={(nextCategory) => {
                        if (!nextCategory) {
                          setSelectedCategory(null);
                          return;
                        }
                        handleCategorySelect(nextCategory);
                      }}
                    />
                  </div>
                ) : (
                  <div className="request-assistance__category-grid">
                    {categories.map((category) => {
                      const Icon = category.icon;
                      const isSelected = selectedCategory === category.id;
                      return (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => handleCategorySelect(category.id)}
                          className={`request-assistance__category-card ${isSelected ? "active" : ""}`}
                        >
                          <Icon size={16} className="request-assistance__category-icon" />
                          <span className="request-assistance__category-label">{category.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>

              {!selectedCategory && (
                <div className="request-assistance__placeholder">
                  <div className="request-assistance__placeholder-illustration" aria-hidden="true">
                    <div className="request-assistance__placeholder-shape">
                      <FileText size={52} />
                    </div>
                    <div className="request-assistance__placeholder-chip">How can we help you today?</div>
                  </div>
                  <p>Select a request type above to continue with the form.</p>
                </div>
              )}

              {/* Request Form */}
              {selectedCategory && (
                <section
                  key={selectedCategory}
                  className={`request-assistance__form-container ${shouldAnimateForm ? 'request-assistance__form-container--animated' : ''}`}
                >
                  <div className="request-assistance__form-header">
                    <h3>{selectedCategoryData?.label}</h3>
                    <p className="request-assistance__form-hint">
                      {selectedCategoryData?.description || 'Please provide detailed information about your request'}
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="request-assistance__form" noValidate>
              {/* Request Details Section */}
              <div className="request-assistance__section">
                <h4 className="request-assistance__section-title">Request Details</h4>

                <div className={`request-assistance__form-group ${validationErrors.urgency ? 'request-assistance__form-group--error' : ''}`}>
                  <label htmlFor="urgency" className="request-assistance__form-label">
                    Urgency Level
                  </label>
                  <RequestUrgencyDropdown
                    value={formData.urgency || 'Normal'}
                    options={URGENCY_OPTIONS}
                    onChange={handleInputChange}
                    hasError={Boolean(validationErrors.urgency)}
                  />
                </div>
                
                <div className={`request-assistance__form-group ${validationErrors.subject ? 'request-assistance__form-group--error' : ''}`}>
                  <label htmlFor="subject" className="request-assistance__form-label">
                    Subject
                  </label>
                  <input
                    id="subject"
                    type="text"
                    name="subject"
                    placeholder="Brief summary of your request"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className={`request-assistance__form-input ${validationErrors.subject ? 'request-assistance__form-input--error' : ''}`}
                    required
                  />
                </div>

                <div className={`request-assistance__form-group ${validationErrors.customRequest ? 'request-assistance__form-group--error' : ''}`}>
                  <label htmlFor="customRequest" className="request-assistance__form-label">
                    Detailed Description
                  </label>
                  <textarea
                    id="customRequest"
                    name="customRequest"
                    placeholder="Please provide a detailed description of your request, including any relevant circumstances, dates, or amounts..."
                    value={formData.customRequest}
                    onChange={handleInputChange}
                    className={`request-assistance__form-textarea ${validationErrors.customRequest ? 'request-assistance__form-input--error' : ''}`}
                    rows={5}
                    required
                  />
                  <p className="request-assistance__form-helper">Minimum 20 characters, maximum 2000 characters</p>
                </div>
              </div>
              
              {/* Submit Section */}
              <div className="request-assistance__form-actions">
                <button
                  type="button"
                  onClick={() => {
                    resetRequestForm();
                  }}
                  className="request-assistance__button request-assistance__button--secondary"
                >
                  Clear Form
                </button>
                <button type="submit" className="request-assistance__button request-assistance__button--primary">
                  <CheckCircle size={16} />
                  Submit Request
                </button>
              </div>
                  </form>
                </section>
              )}
            </section>
          </div>

          <aside className="request-assistance__side-container" aria-label="Additional content panel">
            <h3>What Happens Next?</h3>
            <ul className="request-assistance__side-list">
              <li>
                <span>Your request is logged and assigned a tracking ID.</span>
              </li>
              <li>
                <span>Our team performs an initial assessment and may ask for clarifications.</span>
              </li>
              <li>
                <span>Processing follows your selected request type and urgency level.</span>
              </li>
              <li>
                <span>Updates are sent through email and reflected in My Requests.</span>
              </li>
            </ul>
          </aside>
        </div>

        {showConfirmModal && (
          <div className="request-assistance__modal" role="dialog" aria-modal="true" aria-labelledby="request-confirm-title">
            <button
              type="button"
              className="request-assistance__modal-backdrop"
              onClick={() => setShowConfirmModal(false)}
              aria-label="Close confirmation modal"
            />
            <div className="request-assistance__modal-panel">
              <div className="request-assistance__modal-header">
                <h3 id="request-confirm-title">Confirm Request Submission</h3>
                <p>Review your request details before submitting.</p>
              </div>
              <div className="request-assistance__modal-body">
                <div className="request-assistance__modal-grid">
                  {requestSummaryItems.map((item) => (
                    <div key={item.label}>
                      <span className="request-assistance__modal-label">{item.label}</span>
                      <span className={`request-assistance__modal-value ${item.highlighted ? 'is-highlighted' : ''}`}>{item.value}</span>
                    </div>
                  ))}
                  <div className="request-assistance__modal-grid-full">
                    <span className="request-assistance__modal-label">Detailed Description</span>
                    <p className="request-assistance__modal-description">{formData.customRequest || '—'}</p>
                  </div>
                </div>
              </div>
              <div className="request-assistance__modal-footer">
                <button
                  type="button"
                  className="request-assistance__button request-assistance__button--primary"
                  onClick={handleConfirmSubmission}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Confirm Submission'}
                </button>
                <button
                  type="button"
                  className="request-assistance__button request-assistance__button--secondary"
                  onClick={() => setShowConfirmModal(false)}
                  disabled={isSubmitting}
                >
                  Edit Details
                </button>
                <button
                  type="button"
                  className="request-assistance__modal-link"
                  onClick={() => setShowConfirmModal(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {showSuccessModal && (
          <div className="request-assistance__modal" role="dialog" aria-modal="true" aria-labelledby="request-success-title">
            <button
              type="button"
              className="request-assistance__modal-backdrop"
              onClick={() => {
                setShowSuccessModal(false);
                resetRequestForm();
              }}
              aria-label="Close success modal"
            />
            <div className="request-assistance__modal-panel request-assistance__modal-panel--sm">
              <div className="request-assistance__modal-header">
                <h3 id="request-success-title">Request Submitted Successfully</h3>
                <p>Your request has been submitted successfully.</p>
              </div>
              <div className="request-assistance__modal-body">
                <p className="request-assistance__modal-success-text">
                  You will receive updates via email and can track status in My Requests.
                </p>
                {submissionMeta?.ticketNo ? (
                  <p className="request-assistance__modal-success-meta">Ticket No: {submissionMeta.ticketNo}</p>
                ) : null}
              </div>
              <div className="request-assistance__modal-footer">
                <button
                  type="button"
                  className="request-assistance__button request-assistance__button--primary"
                  onClick={() => navigate('/my-requests')}
                >
                  View My Requests
                </button>
                <button
                  type="button"
                  className="request-assistance__button request-assistance__button--secondary"
                  onClick={() => {
                    setShowSuccessModal(false);
                    resetRequestForm();
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

RequestAssistance.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    digitalId: PropTypes.string,
  }),
  onLogout: PropTypes.func,
};
